import { ErrorPage } from '@/components/ErrorPage';
import { CreatePipelineModal } from '@/components/PipelineModals';
import { TablePageLayout } from '@/layouts/TablePageLayout';
import { useDeletePipeline, usePipelines } from '@/queries/pipelines';
import { formatRelativeTime, getErrorMessage } from '@/utils';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import MoreOutlined from '@ant-design/icons/MoreOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import ShareAltOutlined from '@ant-design/icons/ShareAltOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Dropdown from 'antd/es/dropdown';
import type { MenuProps } from 'antd/es/menu';
import Tag from 'antd/es/tag';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { DataTable, useDataTableState } from 'ui-components';
import type { DataTableColumnType } from 'ui-components';

import type { PipelineWithMetrics } from '@mai/platform-api/src/routes/pipelines.schema';

const WarningText = styled.p`
  color: ${({ theme }) => theme.colorError};
  font-size: ${({ theme }) => theme.fontSize}px;
`;

const PipelineNameLink = styled(Link)`
  font-weight: ${({ theme }) => theme.fontWeightStrong};
  text-decoration: none;
  color: ${({ theme }) => theme.colorLink};

  &:hover {
    color: ${({ theme }) => theme.colorPrimary};
  }
`;

const ActionButton = styled(Button)`
  width: ${({ theme }) => theme.controlHeight}px;
  height: ${({ theme }) => theme.controlHeight}px;
`;

const StatusTag = styled(Tag)<{ $status: string }>`
  font-weight: ${({ theme }) => theme.fontWeightStrong};
`;

export const PipelinesPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { requestState } = useDataTableState({
    filterMap: {
      name: 'nameLike',
    },
  });

  const {
    data: pipelinesData,
    isLoading,
    error,
  } = usePipelines(
    {
      ...requestState,
      teamIds: teamId ? [teamId] : undefined,
    },
    {
      enabled: !!teamId,
    },
  );

  const deletePipelineMutation = useDeletePipeline();

  const tableData = useMemo(() => {
    return pipelinesData?.items || [];
  }, [pipelinesData]);

  const handleCopyId = useCallback(
    async (id: string) => {
      try {
        await navigator.clipboard.writeText(id);
        void message.success('ID copied to clipboard');
      } catch (error) {
        void message.error('Failed to copy ID');
      }
    },
    [message],
  );

  const handleDeletePipeline = useCallback(
    (pipelineId: string, pipelineName: string) => {
      void modal.confirm({
        title: 'Delete Pipeline',
        content: (
          <div>
            <p>
              Are you sure you want to delete the pipeline{' '}
              <strong>"{pipelineName}"</strong>?
            </p>
            <WarningText>
              <strong>Warning:</strong> This action cannot be undone. All
              configuration versions and execution history associated with this
              pipeline will be permanently deleted.
            </WarningText>
          </div>
        ),
        okText: 'Delete Pipeline',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await deletePipelineMutation.mutateAsync(pipelineId);
            void message.success(
              `Pipeline "${pipelineName}" has been deleted successfully`,
            );
          } catch (error) {
            const errorMessage = getErrorMessage(
              error,
              'Failed to delete pipeline',
            );
            void message.error(errorMessage);
          }
        },
      });
    },
    [deletePipelineMutation, message, modal],
  );

  // Memoized status calculation functions
  const getStatusColor = useCallback(
    (
      totalRuns: number,
      successRate: number,
    ): 'success' | 'warning' | 'error' | 'default' => {
      if (totalRuns === 0) return 'default';
      if (successRate >= 90) return 'success';
      if (successRate >= 70) return 'warning';
      return 'error';
    },
    [],
  );

  const getStatusText = useCallback(
    (totalRuns: number, successRate: number): string => {
      if (totalRuns === 0) return 'Not run';
      if (successRate >= 90) return 'Healthy';
      if (successRate >= 70) return 'Issues';
      return 'Failing';
    },
    [],
  );

  // Memoized event handlers
  const handleSharePipeline = useCallback(
    (pipelineId: string) => {
      const url = `${window.location.origin}/team/${teamId}/pipelines/${pipelineId}`;
      void navigator.clipboard.writeText(url);
      void message.success('Pipeline URL copied to clipboard');
    },
    [teamId, message],
  );

  // Memoized columns with stable references
  const columns = useMemo<DataTableColumnType<PipelineWithMetrics>[]>(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        render: (_, record) => (
          <PipelineNameLink
            to={`/team/${teamId}/pipelines/${record.pipelineId}`}
          >
            {record.name}
          </PipelineNameLink>
        ),
        sorter: true,
        filter: {
          type: 'search',
          placeholder: 'Search by name...',
        },
      },
      {
        title: 'Status',
        key: 'status',
        width: 120,
        render: (_, record) => (
          <StatusTag
            color={getStatusColor(record.totalRuns, record.successRate)}
            $status={getStatusText(record.totalRuns, record.successRate)}
          >
            {getStatusText(record.totalRuns, record.successRate)}
          </StatusTag>
        ),
      },
      {
        title: 'Total Runs',
        dataIndex: 'totalRuns',
        key: 'totalRuns',
        width: 100,
        align: 'right',
        render: (count: number) => count.toLocaleString(),
      },
      {
        title: 'Success Rate',
        key: 'successRate',
        width: 120,
        align: 'right',
        render: (_, record) => {
          if (record.totalRuns === 0) return '—';
          return `${Math.round(record.successRate)}%`;
        },
      },
      {
        title: 'Last Run',
        dataIndex: 'lastRunAt',
        key: 'lastRunAt',
        width: 150,
        render: (date: Date | null) =>
          date ? formatRelativeTime(date) : 'Never',
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150,
        render: (date: Date) => formatRelativeTime(date),
        sorter: true,
      },
      {
        title: 'Last Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 150,
        render: (date: Date) => formatRelativeTime(date),
        sorter: true,
      },
      {
        title: '',
        key: 'actions',
        align: 'center',
        width: 80,
        render: (_, record) => {
          const items: MenuProps['items'] = [
            {
              key: 'copy-id',
              icon: <CopyOutlined />,
              label: 'Copy ID',
              onClick: () => void handleCopyId(record.pipelineId),
            },
            {
              key: 'share',
              icon: <ShareAltOutlined />,
              label: 'Share Pipeline',
              onClick: () => handleSharePipeline(record.pipelineId),
            },
            {
              type: 'divider',
            },
            {
              key: 'delete',
              icon: <DeleteOutlined />,
              label: 'Delete Pipeline',
              danger: true,
              onClick: () =>
                handleDeletePipeline(record.pipelineId, record.name),
            },
          ];

          return (
            <Dropdown
              menu={{ items }}
              trigger={['click']}
              placement="bottomRight"
            >
              <ActionButton type="text" icon={<MoreOutlined />} size="small" />
            </Dropdown>
          );
        },
      },
    ],
    [
      teamId,
      getStatusColor,
      getStatusText,
      handleCopyId,
      handleSharePipeline,
      handleDeletePipeline,
    ],
  );

  // Check for missing teamId after all hooks
  if (!teamId) {
    return (
      <ErrorPage
        title="Team Not Found"
        message="Unable to load pipelines. Please make sure you're accessing a valid team."
        backButtonText="Go to Teams"
        onBack={() => (window.location.href = '/')}
      />
    );
  }

  if (error) {
    return (
      <div>
        <h2>Error Loading Pipelines</h2>
        <p>There was an error loading the pipelines. Please try again.</p>
      </div>
    );
  }

  return (
    <>
      <TablePageLayout
        title="Pipelines"
        description="Manage and monitor your data processing pipelines."
        actions={[
          {
            label: 'Create Pipeline',
            type: 'primary',
            icon: <PlusOutlined />,
            onClick: () => {
              setIsCreateModalOpen(true);
            },
          },
        ]}
      >
        <DataTable<PipelineWithMetrics>
          columns={columns}
          data={tableData}
          loading={isLoading}
          rowKey="pipelineId"
          maxVisibleFilters={3}
          pagination={{
            current: pipelinesData?.pagination?.page || 1,
            pageSize: pipelinesData?.pagination?.pageSize || 20,
            total: pipelinesData?.pagination?.totalItems || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          emptyState={{
            icon: <ShareAltOutlined />,
            title: 'No pipelines found',
            description: 'Get started by creating your first pipeline.',
            action: {
              label: 'Create Your First Pipeline',
              onClick: () => {
                setIsCreateModalOpen(true);
              },
              icon: <PlusOutlined />,
            },
            overrideOnFiltersPresent: {
              title: 'No pipelines match your filters',
              description: 'Try adjusting your filters to see more results.',
              action: undefined,
            },
          }}
        />
      </TablePageLayout>

      {teamId && (
        <CreatePipelineModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          teamId={teamId}
          onSuccess={(pipelineId) => {
            void navigate(`/team/${teamId}/pipelines/${pipelineId}`);
          }}
        />
      )}
    </>
  );
};
